import { type Dispatch, type MouseEvent, type SetStateAction, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import loadable from '@loadable/component';

import { type ClickType } from '@jsmdg/tracking';
import {
    Breakpoint,
    NotificationVariant,
    useBreakpoint,
    useIntersection,
    useSnackbarContext,
} from '@jsmdg/yoshi';
import { isClusteredBox } from '../../../shared/product-safeguards';
import { useBuybox } from '../../hooks/useBuybox';
import { useProduct } from '../../hooks/useProduct';
import { useRedemptionCart } from '../../hooks/useRedemptionCart';
import { Buybox } from '../Buybox';
import { CartLinkSnackbarContent } from '../CartLinkSnackbarContent/CartLinkSnackbarContent';
import { trackScrollToClusterClicked } from './buyboxContainer.tracking';

const CookieWarningModal = loadable(async () => import('../CookieWarningModal/CookieWarningModal'));
const LoadableBuyboxCallToAction = loadable(async () => import('../Buybox/BuyboxCallToAction'));

const MixedShoppingCartErrorModal = loadable(
    async () => import('../BuyboxErrorModal/MixedShoppingCartErrorModal'),
);

const AddItemToCartErrorModal = loadable(
    async () => import('../BuyboxErrorModal/AddItemToCartErrorModal'),
);

type BuyboxContainerProps = {
    readonly handleWishlistButtonClick: () => void;
    readonly boxClusterAreaRef: React.RefObject<HTMLElement>;
};

const messages = defineMessages({
    isProductIntheCartErrorMessagePart1: {
        defaultMessage: 'Du hast bereits ein Ticket für dieses Erlebnis im Warenkorb.',
    },
    isProductIntheCartErrorMessagePart2: {
        defaultMessage: 'Die Anzahl der Tickets kannst du direkt in deinem {cartLink} angleichen.',
    },
    isProductNotIntheCartErrorMessagePart1: {
        defaultMessage: 'Du hast bereits ein Ticket für ein anderes Erlebnis im Warenkorb.',
    },
    isProductNotIntheCartErrorMessagePart2: {
        defaultMessage:
            'Falls du deine Wahl ändern möchtest, musst du bitte dieses zunächst entfernen.',
    },
    a11YCloseErrorMessage: {
        defaultMessage: 'Schließen',
    },
    bookAppointment: {
        defaultMessage: 'Termin buchen',
    },
    addToCart: {
        defaultMessage: 'In den Warenkorb',
    },
    goToCluster: {
        defaultMessage: 'Zu den Einzelerlebnissen',
    },
});

const BuyboxContainer = ({
    boxClusterAreaRef,
    handleWishlistButtonClick,
}: BuyboxContainerProps): JSX.Element => {
    const intl = useIntl();
    const { openSnackbar } = useSnackbarContext();
    const [isDiscountSelectOpen, setIsDiscountSelectOpen] = useState(false);
    const upToSMBreakpoint = useBreakpoint(Breakpoint.SM);
    const [isIntersecting, setTargetElement] = useIntersection({
        rootMargin: '0px',
        triggerOnce: false,
        root: undefined,
        threshold: 0.75,
    }) as [boolean, Dispatch<SetStateAction<HTMLElement | null>>];

    const { isProductInRedemptionCart, isRedemptionCartFilled } = useRedemptionCart();

    const buyboxProps = useBuybox();
    const product = useProduct();

    const {
        bookableLocationsCount,
        clusterType,
        handleAddToCartClick,
        handleGotoExperiences,
        handleModalClose,
        hasAddToCartError,
        hasAddingMultipleItemsError,
        hasCookieWarning,
        hasMixedShoppingCartError,
        locations,
        purchaseType,
        ticketPurchaseIsSelected,
        voucherClusterIsSelected,
    } = buyboxProps;

    const isCheckoutDisabled =
        buyboxProps.isAddToCartOrRedemptionDisabled(purchaseType) || isDiscountSelectOpen;

    useEffect(() => {
        if (hasAddingMultipleItemsError || isRedemptionCartFilled) {
            let errorMessage = {
                title: messages.isProductNotIntheCartErrorMessagePart1,
                body: messages.isProductNotIntheCartErrorMessagePart2,
            };
            if (isProductInRedemptionCart) {
                errorMessage = {
                    title: messages.isProductIntheCartErrorMessagePart1,
                    body: messages.isProductIntheCartErrorMessagePart2,
                };
            }

            openSnackbar({
                content: <CartLinkSnackbarContent errorMessage={errorMessage} />,
                variant: NotificationVariant.Error,
                a11yTextCloseButton: intl.formatMessage(messages.a11YCloseErrorMessage),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAddingMultipleItemsError, isRedemptionCartFilled]);

    let callToActionText: string;

    if (ticketPurchaseIsSelected(purchaseType)) {
        callToActionText = intl.formatMessage(messages.bookAppointment);
    } else if (!voucherClusterIsSelected(clusterType) && isClusteredBox(product)) {
        callToActionText = intl.formatMessage(messages.goToCluster);
    } else {
        callToActionText = intl.formatMessage(messages.addToCart);
    }

    const scrollToCluster = () => {
        boxClusterAreaRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const handleBuyboxButtonClick = (
        event?: MouseEvent<HTMLButtonElement>,
        clickType?: ClickType,
    ): void | Promise<void> => {
        if (isClusteredBox(product) && !voucherClusterIsSelected(clusterType)) {
            scrollToCluster();
            trackScrollToClusterClicked(clickType);
            return;
        }

        if (!ticketPurchaseIsSelected(purchaseType)) {
            handleAddToCartClick(event, undefined, clickType);
            return;
        }

        const experienceId = locations.find(location => location.activeAppointments)?.experienceId;
        if (bookableLocationsCount === 1 && Boolean(experienceId)) {
            window.location.assign(`/availabilities/${product.productId}/${experienceId}`);
        } else {
            handleGotoExperiences({
                event,
                clickType,
                clickText: intl.formatMessage(messages.bookAppointment),
            });
        }
    };

    return (
        <>
            {hasMixedShoppingCartError && (
                <MixedShoppingCartErrorModal
                    isOpen={hasMixedShoppingCartError}
                    onRequestClose={handleModalClose}
                />
            )}
            {hasAddToCartError && (
                <AddItemToCartErrorModal
                    isOpen={hasAddToCartError}
                    onRequestClose={handleModalClose}
                />
            )}
            {hasCookieWarning && (
                <CookieWarningModal onRequestClose={buyboxProps.handleModalClose} />
            )}
            <Buybox
                handleWishlistButtonClick={handleWishlistButtonClick}
                setTargetElement={setTargetElement}
                setIsDiscountSelectOpen={setIsDiscountSelectOpen}
                isDiscountSelectOpen={isDiscountSelectOpen}
                handleBuyboxButtonClick={handleBuyboxButtonClick}
                isCheckoutDisabled={isCheckoutDisabled}
                callToActionText={callToActionText}
                buyboxProps={buyboxProps}
            />
            {!isCheckoutDisabled && !upToSMBreakpoint && (
                <LoadableBuyboxCallToAction
                    shouldHide={isIntersecting}
                    isCheckoutDisabled={isCheckoutDisabled}
                    handleBuyboxButtonClick={handleBuyboxButtonClick}
                    isDisabled={isDiscountSelectOpen}
                >
                    {callToActionText}
                </LoadableBuyboxCallToAction>
            )}
        </>
    );
};

export { BuyboxContainer };
